/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * StatBid technical-specs tabs (snippets/statbid-product-specs.liquid).
 * Delegated controller — works for any number of widgets on the page (PDP, quick-view).
 * Accessible: click + Left/Right/Home/End arrow-key roving focus, aria-selected sync.
 * No per-instance init, no global IDs — replaces the prod theme's brittle
 * getElementById('defaultOpen').click() pattern.
 */
(function () {
  function panelFor(tab) {
    return document.getElementById(tab.getAttribute('aria-controls'));
  }

  function activate(tablist, tab, focus) {
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
    tabs.forEach(function (t) {
      var selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.setAttribute('tabindex', selected ? '0' : '-1');
      var panel = panelFor(t);
      if (panel) { panel.hidden = !selected; }
    });
    if (focus) { tab.focus(); }
  }

  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-statbid-specs] [role="tab"]');
    if (!tab) { return; }
    activate(tab.closest('[role="tablist"]'), tab, false);
  });

  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest('[data-statbid-specs] [role="tab"]');
    if (!tab) { return; }
    var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (keys.indexOf(e.key) === -1) { return; }
    e.preventDefault();
    var tablist = tab.closest('[role="tablist"]');
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
    var i = tabs.indexOf(tab);
    var next;
    if (e.key === 'ArrowLeft') { next = tabs[(i - 1 + tabs.length) % tabs.length]; }
    else if (e.key === 'ArrowRight') { next = tabs[(i + 1) % tabs.length]; }
    else if (e.key === 'Home') { next = tabs[0]; }
    else { next = tabs[tabs.length - 1]; }
    activate(tablist, next, true);
  });
})();

/**
 * A11y #15 — collapsible content hidden from AT + tab order when collapsed.
 *
 * The theme's collapsible JS (CollapsibleManager in theme.js) only toggles
 * aria-hidden if the content element ALREADY has the attribute (a
 * `hasAttribute("aria-hidden")` guard in _open/_close). Several consumers ship
 * the content WITHOUT it (footer, PDP card__collapsible, faq), so their collapsed
 * content stays exposed to screen readers AND keyboard-focusable behind the
 * visual collapse. NVDA reads it; Tab lands in it. (main-collection's filter
 * accordions ship aria-hidden and so already work.)
 *
 * Rather than patch each template, we normalize at the component level: keep every
 * collapsible's content in sync with its toggle's state. We manage BOTH aria-hidden
 * (AT tree) and `inert` (removes from tab order + AT). Once we add aria-hidden, the
 * theme's guard goes true and it co-manages that attribute on subsequent toggles —
 * we still re-derive and converge, and `inert` is ours alone.
 *
 * "Effectively collapsed" = toggle is aria-expanded="false" AND not [disabled].
 * The [disabled] exclusion is load-bearing for the footer: at desktop the theme
 * DISABLES the toggle and shows the content always-open (FooterSection.
 * _setupCollapsibles flips [disabled] by breakpoint), so a disabled toggle means
 * the content IS available and must NOT be hidden.
 *
 * State is driven by THREE actors we don't control — the theme's click handler
 * (flips aria-expanded), the footer's breakpoint setup (flips [disabled], on a
 * resize listener that fires no DOM event WE can hook), and direct API. A click/
 * resize guess misses cases (e.g. the footer disabling a toggle at first desktop
 * paint emits neither a click nor, necessarily, after our initial pass). So we
 * observe the toggles directly with a MutationObserver on aria-expanded + disabled
 * and re-sync whatever actually changed — no guessing which event caused it.
 *
 * Focus safety: marking an element `inert` while focus lives inside it silently
 * blurs the user to <body>. Before hiding, if the content contains the active
 * element, we move focus to the toggle first (the natural place — it's what the
 * user would tab back to).
 */
(function () {
  var SELECTOR = '[data-action="toggle-collapsible"]';

  function contentFor(toggle) {
    var id = toggle.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  // Collection FILTER collapsibles are excluded: the theme already manages their
  // aria-hidden, and the theme's own filter/collapsible JS fights our inert (it
  // measures height + re-renders the facet list on filter changes), which made the
  // facet contents vanish after load when we inert-ed them. #15's scope was always
  // footer/PDP/faq (the filters "already work" per the original analysis); this
  // guard makes that explicit so the normalizer never touches the filter accordions.
  var FILTER_EXCLUDE = '.collection__filter-group-name, .collection__filter-link';

  function syncOne(toggle) {
    if (toggle.matches(FILTER_EXCLUDE)) { return; }
    var content = contentFor(toggle);
    if (!content) { return; }
    var collapsed = toggle.getAttribute('aria-expanded') === 'false' &&
                    !toggle.hasAttribute('disabled');
    if (collapsed) {
      // Don't strand focus inside content we're about to make inert.
      if (content.contains(document.activeElement)) {
        toggle.focus();
      }
      content.setAttribute('aria-hidden', 'true');
      content.setAttribute('inert', '');
    } else {
      content.removeAttribute('aria-hidden');
      content.removeAttribute('inert');
    }
  }

  function syncAll() {
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), syncOne);
  }

  // Re-sync a toggle whenever its own aria-expanded/disabled changes, regardless
  // of which actor changed it. This subsumes the old click + resize guesswork.
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.target.matches && m.target.matches(SELECTOR)) {
        syncOne(m.target);
      }
    });
  });

  function start() {
    syncAll();
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'disabled']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * A11y #33 — quantity stepper change not announced (4.1.3 Status Messages).
 *
 * The theme's <quantity-picker> (theme.js) handles +/- by calling stepUp()/
 * stepDown() on the <input type="number"> and dispatching a `change` event, but
 * nothing is announced. NVDA does not reliably read a number input whose value
 * changed programmatically (it announces values the user TYPES, not ones stepped
 * under them), so a screen-reader user gets no confirmation the quantity changed.
 *
 * Fix: one shared visually-hidden status region. We listen (delegated) for
 * `change` on any quantity-picker [name="quantity"] input — the same event the
 * picker already fires on every +/- click and on manual typing — and write
 * "Quantity: N" into the region. One global region covers every picker on the page
 * (PDP + quick-view modal); no per-instance wiring, no theme-file edit. Reuses the
 * theme's existing `.visually-hidden` utility (theme.css).
 *
 * Notes:
 * - role="status" carries an implicit aria-live="polite"; we do NOT also set
 *   aria-live, to avoid AT reading it as two regions and double-announcing.
 * - The region is created empty at startup (not lazily on first change): a live
 *   region populated in the same tick it's inserted is sometimes not yet observed
 *   by AT, dropping the first announcement.
 * - We clear then set on the next frame so that re-announcing the SAME value still
 *   registers as a change. The component re-dispatches `change` after clamping an
 *   invalid entry (stepDown() in _onInputValueChanged/_onInputFocusOut), which can
 *   land on a value equal to the last announced one; without the clear, aria-live
 *   stays silent because textContent didn't change.
 * - "Quantity:" is hardcoded English. Every other SR string in this theme comes
 *   from a `| t` locale key; this one does not. Acceptable while the store is
 *   single-locale — revisit (render a translated string via a data-attribute) if a
 *   second locale is ever added.
 */
(function () {
  var region = null;

  document.addEventListener('DOMContentLoaded', function () {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    document.body.appendChild(region);
  });

  function announce(message) {
    if (!region) { return; }
    region.textContent = '';
    window.requestAnimationFrame(function () {
      region.textContent = message;
    });
  }

  document.addEventListener('change', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') { return; }
    var input = e.target.closest('quantity-picker [name="quantity"]');
    if (!input) { return; }
    var value = input.value;
    if (value === '') { return; }
    announce('Quantity: ' + value);
  });
})();

/**
 * A11y #18 — search toggle's aria-expanded never updated.
 *
 * The condensed-search toggle ([data-action="toggle-search"]) ships
 * aria-expanded="false" but the theme's JS (SearchBar.toggleMobileSearch) only
 * flips CLASSES, never the attribute — so AT always hears "collapsed" even when
 * the search panel is open. Rather than patch every open/close path in the vendor
 * JS (toggle click, close button, unfix...), we observe the single source of
 * truth the theme already maintains: the `header--search-expanded` class on
 * .header (added/removed on every open/close). Mirror it onto the toggle's
 * aria-expanded. One observer, all paths covered, no vendor edit.
 */
(function () {
  function sync(header) {
    var toggle = document.querySelector('[data-action="toggle-search"]');
    if (!toggle) { return; }
    var open = header.classList.contains('header--search-expanded');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function start() {
    var header = document.querySelector('.header');
    if (!header) { return; }
    sync(header);
    new MutationObserver(function () { sync(header); })
      .observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * A11y #21 — grid/list layout buttons' aria-pressed kept in sync.
 *
 * The collection layout toggles (.collection__layout-button) get aria-pressed in
 * Liquid for the initial state, but the theme's _changeLayout JS only swaps the
 * `is-selected` CLASS on click (and on its init .click()), never the attribute.
 * Mirror is-selected → aria-pressed via a MutationObserver so the announced
 * pressed state tracks the active view. Override layer, no vendor JS edit.
 */
(function () {
  function syncButton(btn) {
    btn.setAttribute('aria-pressed', btn.classList.contains('is-selected') ? 'true' : 'false');
  }

  function start() {
    var buttons = document.querySelectorAll('.collection__layout-button');
    if (!buttons.length) { return; }
    Array.prototype.forEach.call(buttons, function (btn) {
      syncButton(btn);
      new MutationObserver(function () { syncButton(btn); })
        .observe(btn, { attributes: true, attributeFilter: ['class'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * A11y #27 — value-picker (Sort by / Display by) Esc-to-close + focus return.
 *
 * The theme's ValuePicker already closes on tab-out (_onFocusOut) and has a close
 * button, so the keyboard-trap core is handled. But it has NO Escape handler and
 * doesn't return focus to the opener — both named in the finding. We add them in
 * the override layer: on Escape, find an OPEN value-picker (its open-toggler is
 * aria-expanded="true"), click that toggler to trigger the theme's own close path,
 * and move focus back to it. Covers sort-by, display-by, and any other ValuePicker.
 */
(function () {
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') { return; }
    var openToggler = document.querySelector(
      '[data-action="open-value-picker"][aria-expanded="true"]'
    );
    if (!openToggler) { return; }
    openToggler.click();      // theme's _toggle → _close (sets aria-expanded false)
    openToggler.focus();      // return focus to the control that opened the menu
  });
})();

/**
 * A11y — Flickity off-screen carousel cells are aria-hidden but still focusable.
 *
 * Flickity (bundled in theme.js) sets aria-hidden="true" on cells scrolled off the
 * viewport but leaves the cell's inner links/buttons keyboard-focusable. That trips
 * the axe `aria-hidden-focus` rule ("aria-hidden element contains focusable
 * descendants" — Lighthouse Accessibility AND the Agentic Browsing category) and lets
 * a keyboard user tab onto a card they can't see. The collection-list/brand carousel
 * was handled in theme.js (#2) via cell tabindex, which works there because the cell
 * IS the link; the PRODUCT carousels (recommendations / recently-viewed) have
 * multi-focusable cards, so we mirror the cell's aria-hidden onto `inert` — which
 * drops the whole subtree from BOTH the tab order and the a11y tree in one attribute.
 *
 * Scoped to Flickity cells only (direct children of .flickity-slider), so modals,
 * mega-menus, drawers etc. that also toggle aria-hidden are never touched. `inert`
 * changes no layout, so it can't cause a #15-style "content vanished" regression; and
 * the cells it inerts are off-screen, so disabling their pointer events is a no-op.
 */
(function () {
  function isCell(el) {
    return el.nodeType === 1 && el.parentElement &&
           el.parentElement.classList.contains('flickity-slider');
  }
  function sync(cell) {
    var hidden = cell.getAttribute('aria-hidden') === 'true';
    var hasInert = cell.hasAttribute('inert');
    if (hidden && !hasInert) { cell.setAttribute('inert', ''); }       // idempotent —
    else if (!hidden && hasInert) { cell.removeAttribute('inert'); }   // no redundant writes
  }
  function sweep() {
    Array.prototype.forEach.call(document.querySelectorAll('.flickity-slider > *'), sync);
  }
  // Ongoing: Flickity flips aria-hidden on scroll/select — mirror it onto inert.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) { if (isCell(m.target)) { sync(m.target); } });
  }).observe(document.documentElement, {
    subtree: true, attributes: true, attributeFilter: ['aria-hidden']
  });
  // Initial load is the hard part: the recommendations / recently-viewed carousels are
  // AJAX-loaded (and re-fetched) per page load at unpredictable times, and Flickity sets
  // aria-hidden as it BUILDS the slider — a one-shot sweep or even a childList observer
  // races it. A short bounded interval brute-forces coverage across the page's load/settle
  // window (the same window Lighthouse + AT read in), then stops so there's no ongoing cost.
  // `sync` is idempotent, so re-sweeping is cheap.
  sweep();
  var ticks = 0, timer = window.setInterval(function () {
    sweep();
    if (++ticks >= 40) { window.clearInterval(timer); }  // ~10s at 250ms
  }, 250);
})();

